/**
 * 메일 첨부 — 트리거 버튼(트리거 모드) 또는 첨부 칩 리스트(표시 모드).
 *
 * 두 가지 모드:
 *   mode="trigger"   — 푸터의 📎 버튼. 클릭하면 파일 선택 다이얼로그.
 *                       props: button (children for trigger), onFilesAdded(files: File[])
 *   mode="display"   — 본문 아래 첨부 칩 리스트. props: attachments, onRemove
 *
 * <b>드래그&드롭은 여기가 아니라 MailWriteModal 이 처리한다.</b> 작성 영역 전체
 * (헤더·본문·푸터)를 드롭존으로 쓰기 때문에 이 컴포넌트 안에 가둘 수 없다 —
 * MailWriteModal 의 handleDrop / dragActive 오버레이를 볼 것. 두 경로 모두
 * handleAttachmentsAdded 로 모여 25MB 합계 검증을 거친다.
 */
import React, { useRef } from "react";
import { Box, Chip, IconButton, Tooltip, Typography } from "@mui/material";
import InsertDriveFileRoundedIcon from "@mui/icons-material/InsertDriveFileRounded";
import PictureAsPdfRoundedIcon    from "@mui/icons-material/PictureAsPdfRounded";
import ImageRoundedIcon           from "@mui/icons-material/ImageRounded";
import CloseRoundedIcon           from "@mui/icons-material/CloseRounded";
import { formatFileSize as formatSize } from "../../../../util/numberFormat";

function fileIcon(mimeType) {
    if (!mimeType) return <InsertDriveFileRoundedIcon sx={{ fontSize: 14 }} />;
    if (mimeType.includes("pdf")) return <PictureAsPdfRoundedIcon sx={{ fontSize: 14, color: "#ef4444" }} />;
    if (mimeType.startsWith("image/")) return <ImageRoundedIcon sx={{ fontSize: 14, color: "#3b82f6" }} />;
    return <InsertDriveFileRoundedIcon sx={{ fontSize: 14, color: "#64748b" }} />;
}


export default function AttachmentDropzone({
    mode = "display",
    attachments = [],
    onRemove,
    onFilesAdded,
    button,
}) {
    const inputRef = useRef();

    if (mode === "trigger") {
        return (
            <>
                <Box component="label" sx={{ display: "inline-flex", alignItems: "center" }}>
                    {button ?? (
                        <Tooltip title="첨부파일 추가">
                            <IconButton size="small" component="span">📎</IconButton>
                        </Tooltip>
                    )}
                    <input
                        ref={inputRef}
                        type="file" multiple hidden
                        onChange={(e) => {
                            const files = Array.from(e.target.files || []);
                            if (!files.length) return;
                            onFilesAdded?.(files);
                            e.target.value = "";
                        }}
                    />
                </Box>
            </>
        );
    }

    // 표시 모드 — 첨부 칩 리스트
    if (!attachments.length) return null;
    return (
        <Box sx={{
            display: "flex", flexWrap: "wrap", gap: 0.75,
            p: 1, borderRadius: 1.5, bgcolor: "#f8fafc",
            border: "1px solid #e2e8f0",
        }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", mr: 0.5, alignSelf: "center" }}>
                첨부 {attachments.length}
            </Typography>
            {attachments.map((a) => (
                <Chip
                    key={a.id}
                    icon={fileIcon(a.mimeType)}
                    label={
                        <Box component="span" sx={{ fontSize: 11.5 }}>
                            {a.filename}
                            <Box component="span" sx={{ ml: 0.6, color: "#94a3b8" }}>
                                {formatSize(a.sizeBytes)}
                            </Box>
                        </Box>
                    }
                    size="small"
                    onDelete={onRemove ? () => onRemove(a.id) : undefined}
                    deleteIcon={<CloseRoundedIcon sx={{ fontSize: 13 }} />}
                    sx={{ bgcolor: "#fff", border: "1px solid #e2e8f0" }}
                />
            ))}
        </Box>
    );
}
